/** @typedef {'PRIVATE' | 'COMPANY'} CustomerType */

/**
 * Optional BCP 47 locale for `preferences.type.culture` (e.g. `sv-SE`).
 * @param {string} [countryCode]
 */
export function cultureFromCountryCode(countryCode) {
   if (!countryCode) {
      return undefined
   }
   const upper = String(countryCode).toUpperCase()
   const map = {
      SE: 'sv-SE',
      NO: 'nb-NO',
      DK: 'da-DK',
      FI: 'fi-FI',
      DE: 'de-DE',
   }
   return map[upper]
}

/**
 * Map flat UI fields to `SignUpInput` (nested `billingAddress` with `SignUpPropertyInput`).
 * See `schema.graphql`: SignUpInput, SignUpBillingAddressInput, SignUpPropertyInput.
 *
 * **Preferences** (communication opt-in) map to GraphQL as:
 * `preferences: { type: { acceptsEmail, acceptsPostal, acceptsSms, culture } }`.
 * Pass `preferences` to override the whole object; otherwise it is built from the boolean flags.
 *
 * @param {Object} params
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.emailAddress
 * @param {string} params.password
 * @param {CustomerType} params.customerType
 * @param {string} params.mobilePhone
 * @param {object} [params.channel] – active channel; `countryCode` from `channel.country.code` (default `SE`); `preferences.type.culture` from `channel.defaultLanguage.culture` when `culture` param omitted
 * @param {string} [params.pid] – private customers when channel requires PID
 * @param {string} [params.organizationNumber] – company customers when required
 * @param {string} [params.companyName] – company customers
 * @param {string} [params.streetName] – company customers billing address
 * @param {string} [params.city] – company customers billing address
 * @param {string} [params.postalCode] – company customers billing address
 * @param {boolean} [params.acceptsEmail]
 * @param {boolean} [params.acceptsSms]
 * @param {boolean} [params.acceptsPostal]
 * @param {string} [params.culture] – e.g. `sv-SE`; when omitted, uses `channel.defaultLanguage.culture`, then `cultureFromCountryCode` from channel country
 * @param {object} [params.preferences] – full `SignUpPreferencesInput`; wins over the flags above
 */
export function buildRegisterCustomerInput({
   firstName,
   lastName,
   emailAddress,
   password,
   customerType,
   mobilePhone,
   pid = '',
   organizationNumber = '',
   companyName = '',
   streetName = '',
   city = '',
   postalCode = '',
   acceptsEmail = true,
   acceptsSms = true,
   acceptsPostal = false,
   culture: cultureExplicit,
   preferences: preferencesInput,
   channel,
}) {
   const prop = value => ({ value: value == null ? '' : String(value) })

   const rawCountryCode = channel?.country?.code
   const countryCode =
      rawCountryCode && String(rawCountryCode).trim()
         ? String(rawCountryCode).trim().toUpperCase()
         : 'SE'

   const rawCulture = channel?.defaultLanguage?.culture
   const cultureFromDefaultLanguage =
      rawCulture && String(rawCulture).trim()
         ? String(rawCulture).trim()
         : undefined

   const culture =
      cultureExplicit !== undefined && cultureExplicit !== null && cultureExplicit !== ''
         ? cultureExplicit
         : cultureFromDefaultLanguage ?? cultureFromCountryCode(countryCode)

   const preferences =
      preferencesInput != null
         ? preferencesInput
         : {
              type: {
                 acceptsEmail,
                 acceptsSms,
                 acceptsPostal,
                 ...(culture ? { culture } : {}),
              },
           }

   return {
      type: customerType,
      emailAddress,
      password,
      pid: customerType === 'PRIVATE' ? pid : '',
      organizationNumber:
         customerType === 'COMPANY' ? organizationNumber : '',
      externalId: '',
      consents: [],
      preferences,
      billingAddress: {
         countryCode: prop(countryCode),
         firstName: prop(firstName),
         lastName: prop(lastName),
         mobilePhone: prop(mobilePhone),
         company: prop(customerType === 'COMPANY' ? companyName : ''),
         streetName: prop(customerType === 'COMPANY' ? streetName : ''),
         co: prop(''),
         postalCode: prop(customerType === 'COMPANY' ? postalCode : ''),
         city: prop(customerType === 'COMPANY' ? city : ''),
      },
   }
}
